'use strict';

import _ from 'lodash';
import { EventEmitter } from 'events';
import AppDispatcher from '../dispatcher/AppDispatcher';
import HotelConstants from '../constants/HotelConstants';
import { FIREBASE_STRUCTURE } from 'arbitrip-common/general/constants';
import FirebaseManager from 'arbitrip-common/client/utils/Firebase/Manager';
import RecheckAlternative from 'arbitrip-common/client/utils/RecheckAlternative';
import ResultsStore from './ResultsStore';
import HotelActions from '../actions/HotelActions';

const STATUS = {
    IDLE: 'idle',
    BUSY: 'busy',
    SUCCESS: 'success',
    NO_AVAILABILITY: 'no_availability',
};

const { UNAVAILABLE_REASON } = RecheckAlternative;

let _status = STATUS.IDLE;
let _clicked_deal = null;
let _hotel_id = null;
let _lazy_deals = [];
let _lazy_deal_ids = [];
let _raw_recheck_deals = [];
let _deals_to_ban = [];
let _navigation_deal = null;
let _price_change_data = null;
let _unavailable_reason = null;
let _rejected_comparison = null;
let _listener_id = null;
let _is_expired = false;
let _suggest_alternative = false;
let _alternatives_snapshot = null;

function _reset() {
    if (_listener_id) {
        FirebaseManager.recheck.unsubscribe(_listener_id);
    }
    _status = STATUS.IDLE;
    _clicked_deal = null;
    _hotel_id = null;
    _lazy_deals = [];
    _lazy_deal_ids = [];
    _raw_recheck_deals = [];
    _deals_to_ban = [];
    _navigation_deal = null;
    _price_change_data = null;
    _unavailable_reason = null;
    _rejected_comparison = null;
    _listener_id = null;
    _is_expired = false;
    _suggest_alternative = false;
    _alternatives_snapshot = null;
}

function _onFirebaseData(rechecks) {
    if (!rechecks) return;

    const deals = _.get(rechecks, FIREBASE_STRUCTURE.RECHECK.FIELD, []);
    const messages = _.get(rechecks, FIREBASE_STRUCTURE.RECHECK.MESSAGE_TYPE, []);

    if (Array.isArray(deals) && deals.length > 0) {
        deals.forEach((raw_deal) => {
            AppDispatcher.handleAction({
                actionType: HotelConstants.LAZY_RECHECK_DEAL_RECEIVED,
                data: { raw_deal },
            });
        });
    }

    const recheck_completed =
        Array.isArray(messages) &&
        messages.filter((msg) => msg === FIREBASE_STRUCTURE.RECHECK.MESSAGE_TYPE_RECHECK_COMPLETED);
    if (recheck_completed && recheck_completed.length > 0) {
        AppDispatcher.handleAction({
            actionType: HotelConstants.LAZY_RECHECK_COMPLETED,
            data: {},
        });
    }
}

function _onFirebaseError(error) {
    console.error('LazyRecheckStore: Firebase error', error);
    AppDispatcher.handleAction({
        actionType: HotelConstants.LAZY_RECHECK_NO_AVAILABILITY,
        data: {},
    });
}

function _onFirebaseTimeout() {
    console.log('_onFirebaseTimeout');
    AppDispatcher.handleAction({
        actionType: HotelConstants.LAZY_RECHECK_NO_AVAILABILITY,
        data: {},
    });
}

function _banDeals(hotel, deals) {
    if (!hotel || !deals.length) return;
    const snapshot = deals.slice();
    setTimeout(() => snapshot.forEach((deal) => HotelActions.banDeal(hotel, { ...deal, hide: true })), 0);
}

function _handleCompleted() {
    if (_listener_id) {
        FirebaseManager.recheck.unsubscribe(_listener_id);
        _listener_id = null;
    }
    if (_status !== STATUS.BUSY) {
        return;
    }

    const hotel = ResultsStore.getHotelById(_hotel_id);

    if (_raw_recheck_deals.length === 0) {
        _deals_to_ban = _lazy_deals.slice();
        _banDeals(hotel, _deals_to_ban);
        _unavailable_reason = UNAVAILABLE_REASON.NO_ALTERNATIVE;
        _status = STATUS.NO_AVAILABILITY;
        return;
    }

    if (hotel) {
        hotel.addRawRecheckedDeals(ResultsStore.getCurrentSearchSession(), _raw_recheck_deals);
    }

    const returned_ids = new Set(_raw_recheck_deals.map((d) => d.id));
    _deals_to_ban = _lazy_deals.filter((d) => !returned_ids.has(d.id));
    const deals_to_ban_ids = _deals_to_ban.map((d) => d.id);
    _banDeals(hotel, _deals_to_ban);

    const hotel_deals = (hotel && hotel.deals) || [];
    const available_deals = hotel_deals.filter(
        (d) =>
            returned_ids.has(d.id) &&
            d.recheck_id &&
            !deals_to_ban_ids.includes(d.id) &&
            !d.is_lazy_recheck,
    );
    if (available_deals.length === 0) {
        // Returned-but-faulty deals weren't caught by the not-returned filter; ban them too.
        const faulty_returned = _lazy_deals.filter((d) => returned_ids.has(d.id));
        _deals_to_ban = _deals_to_ban.concat(faulty_returned);
        _banDeals(hotel, faulty_returned);
        _unavailable_reason = UNAVAILABLE_REASON.NO_ALTERNATIVE;
        _status = STATUS.NO_AVAILABILITY;
        return;
    }

    const best_alternative = RecheckAlternative.pickBestAcceptableAlternative(_clicked_deal, available_deals);

    // Every alternative degraded the board basis or the cancellation policy without a price drop:
    // treat it as no availability. The rechecked deals stay in the list carrying their new terms,
    // so the original terms can never be booked again.
    if (!best_alternative) {
        _rejected_comparison = RecheckAlternative.buildRejectedComparison(_clicked_deal, available_deals);
        _unavailable_reason = _suggest_alternative
            ? UNAVAILABLE_REASON.ALTERNATIVE_REJECTED
            : UNAVAILABLE_REASON.TERMS_DOWNGRADED;
        _status = STATUS.NO_AVAILABILITY;
        return;
    }

    const { deal: best_deal, comparison } = best_alternative;

    _navigation_deal = best_deal;
    best_deal.superseded = false;
    _status = STATUS.SUCCESS;

    if (comparison.price_changed || comparison.deadline_changed || comparison.board_changed) {
        _price_change_data = {
            original_deal: _clicked_deal,
            best_deal,
            ...comparison,
        };
    }
}

const LazyRecheckStore = _.extend({}, EventEmitter.prototype, {
    emitChange: function () {
        this.emit('change');
    },
    addChangeListener: function (callback) {
        this.on('change', callback);
    },
    removeChangeListener: function (callback) {
        this.removeListener('change', callback);
    },

    getStatus: function () {
        return _status;
    },
    isBusy: function () {
        return _status === STATUS.BUSY;
    },
    isLoadingDeal: function (deal_id) {
        return _status === STATUS.BUSY && _lazy_deal_ids.includes(deal_id);
    },
    getClickedDeal: function () {
        return _clicked_deal;
    },
    getNavigationDeal: function () {
        return _navigation_deal;
    },
    getPriceChangeData: function () {
        return _price_change_data;
    },
    getDealsToBan: function () {
        return _deals_to_ban;
    },
    isNoAvailability: function () {
        return _status === STATUS.NO_AVAILABILITY;
    },
    getUnavailableReason: function () {
        return _unavailable_reason;
    },
    getRejectedComparison: function () {
        return _rejected_comparison;
    },
    getAlternativesSnapshot: function () {
        return _alternatives_snapshot;
    },
    isSuggestAlternativeEnabled: function () {
        return _suggest_alternative;
    },
    isExpired: function () {
        return _is_expired;
    },
    STATUS,
});

AppDispatcher.register(function (payload) {
    const action = payload.action;

    switch (action.actionType) {
        case HotelConstants.LAZY_RECHECK_START: {
            _reset();
            _clicked_deal = action.data.clicked_deal;
            _hotel_id = action.data.hotel_id;
            _lazy_deals = action.data.lazy_deals;
            _suggest_alternative = action.data.suggest_alternative === true;
            _alternatives_snapshot = action.data.alternatives_snapshot || null;
            _lazy_deal_ids = _lazy_deals.map((d) => d.id);
            _status = STATUS.BUSY;
            break;
        }

        case HotelConstants.LAZY_RECHECK_REQUESTED: {
            const { listener_id, wait_time } = action.data;
            _listener_id = listener_id;
            FirebaseManager.recheck.subscribe(
                listener_id,
                _onFirebaseData,
                _onFirebaseError,
                _onFirebaseTimeout,
                wait_time,
            );
            break;
        }

        case HotelConstants.LAZY_RECHECK_DEAL_RECEIVED: {
            const { raw_deal } = action.data;
            if (raw_deal && !_raw_recheck_deals.find((d) => d.id === raw_deal.id)) {
                _raw_recheck_deals.push(raw_deal);
            }
            if (raw_deal?.id === _clicked_deal.id) {
                _handleCompleted();
                break;
            }
            return true;
        }

        case HotelConstants.LAZY_RECHECK_COMPLETED: {
            _handleCompleted();
            break;
        }

        case HotelConstants.LAZY_RECHECK_EXPIRED: {
            _is_expired = true;
            _status = STATUS.IDLE;
            break;
        }

        case HotelConstants.LAZY_RECHECK_NO_AVAILABILITY: {
            if (_listener_id) {
                FirebaseManager.recheck.unsubscribe(_listener_id);
                _listener_id = null;
            }

            // The Firebase subscription timeout still fires after a recheck completed
            // successfully. Banning here would ban the deals of a finished recheck — including
            // the one the user was sent to — restoring their pre-recheck terms and hiding them.
            if (_status !== STATUS.BUSY) {
                break;
            }

            _unavailable_reason = UNAVAILABLE_REASON.NO_ALTERNATIVE;
            _status = STATUS.NO_AVAILABILITY;

            if (_lazy_deals.length > 0) {
                _banDeals(ResultsStore.getHotelById(_hotel_id), _lazy_deals.slice());
            }
            break;
        }

        case HotelConstants.LAZY_RECHECK_CONFIRM_PRICE_CHANGE: {
            _price_change_data = null;
            break;
        }

        case HotelConstants.LAZY_RECHECK_RESET: {
            _reset();
            break;
        }

        default:
            return true;
    }

    LazyRecheckStore.emitChange();
    return true;
});

export default LazyRecheckStore;
